import { TRIAGE_QUESTIONS, type TriageFindings, triageQuestions } from '@orgsim/shared';

type Props = {
  findings: TriageFindings;
  limitedResources: boolean;
  onChange: (patch: Partial<TriageFindings>) => void;
};

type Key = keyof TriageFindings;

// Which answers the algorithm actually reaches for this patient. Everything else is shown
// but disabled, so the instructor can see the step exists without being asked to fill it.
// Derived from triageQuestions() in @orgsim/shared, which is the single place that
// encodes the walk: a field is reachable here exactly when the algorithm would ask it, so
// the editor's greying-out cannot drift from what the run screen asks.
function reachable(f: TriageFindings, limitedResources: boolean): Record<Key, boolean> {
  const asked = new Set(triageQuestions(f, limitedResources).map((row) => row.field));
  return {
    ambulatory: asked.has('ambulatory'),
    lethalInjury: asked.has('lethalInjury'),
    spontaneousBreathing: asked.has('spontaneousBreathing'),
    breathingAfterAirway: asked.has('breathingAfterAirway'),
    respiratoryRateOver30: asked.has('respiratoryRateOver30'),
    spurtingBleeding: asked.has('spurtingBleeding'),
    radialPulseAbsent: asked.has('radialPulseAbsent'),
    followsCommands: asked.has('followsCommands'),
    survivable: asked.has('survivable'),
    acuteSurgicalIndication: true, // second stage, judged by the LNA regardless of the walk
    criticalOnReassessment: true, // second stage, asked at the minor-injury area
  };
}

const FIRST_STAGE: Key[] = [
  'ambulatory',
  'lethalInjury',
  'spontaneousBreathing',
  'breathingAfterAirway',
  'respiratoryRateOver30',
  'spurtingBleeding',
  'radialPulseAbsent',
  'followsCommands',
  'survivable',
];

const SECOND_STAGE: Key[] = ['acuteSurgicalIndication', 'criticalOnReassessment'];

function Row({
  field,
  value,
  enabled,
  onChange,
}: {
  field: Key;
  value: boolean;
  enabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className={`flex items-center gap-2 ${enabled ? '' : 'opacity-40'}`}>
      <span className="flex-1">{TRIAGE_QUESTIONS[field]}</span>
      <div className="flex overflow-hidden rounded border border-neutral-700">
        {[true, false].map((option) => (
          <button
            key={String(option)}
            type="button"
            disabled={!enabled}
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={`px-2 py-0.5 text-xs ${
              value === option ? 'bg-red-600 text-white' : 'bg-neutral-800 hover:bg-neutral-700'
            }`}
          >
            {option ? 'Ja' : 'Nein'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TriageFields({ findings, limitedResources, onChange }: Props) {
  const open = reachable(findings, limitedResources);
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        {FIRST_STAGE.map((field) => (
          <Row
            key={field}
            field={field}
            value={findings[field]}
            enabled={open[field]}
            onChange={(value) => onChange({ [field]: value } as Partial<TriageFindings>)}
          />
        ))}
      </div>
      <div className="space-y-1 border-t border-neutral-800 pt-2">
        <div className="text-xs uppercase tracking-wide text-neutral-500">
          Zweite Stufe, nach der Einordnung
        </div>
        {SECOND_STAGE.map((field) => (
          <Row
            key={field}
            field={field}
            value={findings[field]}
            enabled
            onChange={(value) => onChange({ [field]: value } as Partial<TriageFindings>)}
          />
        ))}
      </div>
    </div>
  );
}
